import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Dialog, DialogContent } from "../ui/dialog";
import { ArrowRight } from "./icons";
import { trackEvent } from "../../lib/analytics";
import { isOtherPopupOpen } from "../../lib/popupGate";
import SessionUrgencyTimer from "./SessionUrgencyTimer";

const STORAGE_PREFIX = "btk_exit_intent_v1:";
const ARM_DELAY_MS = 4000; // wait this long after page load before allowing trigger

function storageKey(pathname) {
  return STORAGE_PREFIX + pathname;
}

function alreadyShown(pathname) {
  try {
    return sessionStorage.getItem(storageKey(pathname)) === "1";
  } catch (e) {
    return false;
  }
}

function markShown(pathname) {
  try {
    sessionStorage.setItem(storageKey(pathname), "1");
  } catch (e) {
    /* noop */
  }
}

/**
 * ExitIntentPopup
 * Shows a Dialog with the same alternative offer the AlternativeOfferBanner
 * displays. Triggered by:
 *   - Desktop: mouseleave toward the top of the viewport
 *   - Any device: tab/app becoming hidden (visibilitychange) as a soft fallback
 * Shown at most once per route per session (sessionStorage).
 */
export default function ExitIntentPopup({
  eyebrow = "Wait — before you go",
  headline,
  subheadline,
  ctaText = "Show Me",
  ctaTo,
  ctaHref,
  dismissText = "No thanks, keep browsing",
  eventPrefix = "exit_intent",
  showUrgencyTimer = false,
  urgencyTimerKey,
  urgencyTimerLabel = "Offer expires in",
}) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const armedRef = useRef(false);

  const trigger = useCallback(
    (source) => {
      if (alreadyShown(location.pathname)) return;
      if (!armedRef.current) return;
      // Don't stack on top of cookie banner / mailing-list / zylvie modal.
      if (isOtherPopupOpen("exit-intent-popup")) return;
      markShown(location.pathname);
      setOpen(true);
      try {
        trackEvent(`${eventPrefix}_popup_shown`, {
          metadata: {
            path: location.pathname,
            destination: ctaTo || ctaHref,
            source,
          },
        });
      } catch (e) {
        /* noop */
      }
    },
    [location.pathname, ctaTo, ctaHref, eventPrefix],
  );

  useEffect(() => {
    if (!headline || (!ctaTo && !ctaHref)) return undefined;
    if (alreadyShown(location.pathname)) return undefined;

    armedRef.current = false;
    const armTimer = setTimeout(() => {
      armedRef.current = true;
    }, ARM_DELAY_MS);

    const onMouseLeave = (e) => {
      // Cursor leaves the viewport toward the top (closing the tab / address bar)
      if (e.clientY <= 0) trigger("mouseleave_top");
    };

    const onVisibility = () => {
      // Mobile / general fallback: tab/app hidden (user switching away)
      if (document.visibilityState === "hidden") trigger("visibility_hidden");
    };

    document.addEventListener("mouseleave", onMouseLeave);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseleave", onMouseLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [trigger, headline, ctaTo, ctaHref, location.pathname]);

  const onCtaClick = () => {
    try {
      trackEvent(`${eventPrefix}_popup_click`, {
        metadata: { path: location.pathname, destination: ctaTo || ctaHref },
      });
    } catch (e) {
      /* noop */
    }
    setOpen(false);
  };

  const onDismiss = () => {
    try {
      trackEvent(`${eventPrefix}_popup_dismiss`, {
        metadata: { path: location.pathname },
      });
    } catch (e) {
      /* noop */
    }
    setOpen(false);
  };

  if (!headline || (!ctaTo && !ctaHref)) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onDismiss();
        else setOpen(v);
      }}
    >
      <DialogContent
        data-testid="exit-intent-popup"
        className="max-w-lg bg-[#0a0a0a] border border-gold/30 text-white p-8 md:p-10"
      >
        <div className="text-center">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-gold/90 px-4 py-1.5 rounded-full border border-gold/30 bg-black/40 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
            {eyebrow}
          </span>

          <h3 className="mt-5 font-display text-2xl md:text-3xl leading-[1.15] tracking-tight">
            {headline}
          </h3>

          {subheadline && (
            <p className="mt-3 text-sm md:text-base text-white/65 leading-relaxed">
              {subheadline}
            </p>
          )}

          {showUrgencyTimer ? (
            <div className="mt-6 flex justify-center">
              <SessionUrgencyTimer
                storageKey={urgencyTimerKey || `popup-${location.pathname}`}
                label={urgencyTimerLabel}
                variant="compact"
              />
            </div>
          ) : null}

          <div className="mt-7 flex flex-col items-center gap-4">
            {ctaTo ? (
              <Link
                to={ctaTo}
                onClick={onCtaClick}
                data-testid="exit-intent-cta"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bk-shimmer text-black text-xs uppercase tracking-[0.22em] font-bold hover:scale-[1.03] transition-transform"
              >
                {ctaText}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : (
              <a
                href={ctaHref}
                onClick={onCtaClick}
                data-testid="exit-intent-cta"
                className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bk-shimmer text-black text-xs uppercase tracking-[0.22em] font-bold hover:scale-[1.03] transition-transform"
              >
                {ctaText}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            )}
            <button
              type="button"
              onClick={onDismiss}
              data-testid="exit-intent-dismiss"
              className="text-xs uppercase tracking-[0.22em] text-white/55 hover:text-white/85 underline-offset-4 hover:underline"
            >
              {dismissText}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
